import { Icon } from '@iconify/react';
import { Card } from '@nextui-org/react';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import React, { useState } from 'react';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

interface FileViewerProps {
  fileUrl: string;
  height?: string;
  className?: string;
  onClose?: () => void;
  filename?: string;
  mimeType?: string;
}

function pathLooksLikePdf(value: string): boolean {
  try {
    return new URL(value, 'https://placeholder.local').pathname
      .toLowerCase()
      .endsWith('.pdf');
  } catch {
    return value.toLowerCase().split('?')[0].endsWith('.pdf');
  }
}

function isPdfFile(
  fileUrl: string,
  filename?: string,
  mimeType?: string
): boolean {
  if (mimeType?.toLowerCase().includes('pdf')) {
    return true;
  }

  return Boolean(
    (filename && pathLooksLikePdf(filename)) || pathLooksLikePdf(fileUrl)
  );
}

const FileViewer: React.FC<FileViewerProps> = ({
  fileUrl,
  height = '100vh',
  className,
  onClose,
  filename,
  mimeType,
}) => {
  const [hasError, setHasError] = useState(false);
  const [useIframe, setUseIframe] = useState(false);

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    sidebarTabs: () => [],
    toolbarPlugin: {
      fullScreenPlugin: {
        onEnterFullScreen: () => {},
        onExitFullScreen: () => {},
      },
    },
  });

  const isPdf = isPdfFile(fileUrl, filename, mimeType);

  return (
    <Card
      className={`w-full overflow-hidden rounded-lg shadow-lg bg-background flex flex-col ${className}`}
      style={{ height }}
    >
      {(onClose || filename) && (
        <div className="flex justify-between items-center p-3 border-b shrink-0">
          <h3 className="text-sm font-medium truncate">{filename || ''}</h3>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close viewer"
            >
              <Icon icon="material-symbols:close" className="text-xl" />
            </button>
          )}
        </div>
      )}
      <div className="flex-1 w-full relative min-h-0 overflow-auto">
        {hasError ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-500">
            <Icon icon="material-symbols:error-outline" className="text-4xl" />
            <p className="text-sm">Unable to load document</p>
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary underline"
            >
              Open in a new tab
            </a>
          </div>
        ) : isPdf && !useIframe ? (
          <Worker
            workerUrl={`https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`}
          >
            <Viewer
              fileUrl={fileUrl}
              plugins={[defaultLayoutPluginInstance]}
              renderError={() => {
                // Signed S3 URLs often fail pdf.js (CORS). The iframe path is
                // what Library used before CSP blocked it.
                queueMicrotask(() => setUseIframe(true));
                return <></>;
              }}
            />
          </Worker>
        ) : (
          <iframe
            src={fileUrl}
            className="w-full h-full border-0"
            title={filename || 'Document viewer'}
            onError={() => setHasError(true)}
          />
        )}
      </div>
    </Card>
  );
};

export default FileViewer;
